import React, { useMemo, useState } from 'react';
import { Button, Dots } from '~/UIKit';
import { EventListView } from './EventList/';
import CalendarComponent from './Calendar/Calendar.component';
import { PRIMARY_BLUE, WHITE } from '../../Constants/Colors.constants';
import { AnimatePresence, motion, PanInfo } from 'framer-motion';
import { CONTAINER_BOX_SHADOW } from '../../Constants/CSS.constants';
import { useMediaQuery, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectLanguage } from '~/store/app';
import { useLanguage } from '~/Hooks';
import { useEventListWrapper } from '~/Screens/EventList/useEventListWrapper';
import { EventEmptyPlaceholder } from './EventEmptyPlaceholder';
import { makeStyles } from '~/UIKit/Style';
import { Checklist } from '~/ExportedComponents/TPD';
import {
  HEADER_HEIGHT_MOBILE,
  HEADER_HEIGHT_MOBILE_WITH_GLOBAL_ALERT,
} from '~/Constants/UI.constants';
import { PrivateLayout } from '~/Layout/PrivateLayout';
import { CalendarFormat } from '@fitogram/react-calendar';
import { selectCurrentWarning } from '~/store/app/warning';

const useStyles = (hasGlobalWarning: boolean) =>
  makeStyles(
    (theme) => {
      const mobileHeightSize = hasGlobalWarning
        ? HEADER_HEIGHT_MOBILE_WITH_GLOBAL_ALERT
        : HEADER_HEIGHT_MOBILE;

      return {
        dashboardContainer: {
          display: 'flex',
          flexDirection: 'column-reverse',
          [theme.breakpoints.up('md')]: {
            flexDirection: 'row',
          },
        },
        root: {
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: `calc(100vh - ${mobileHeightSize}px - 3px)`,

          [theme.breakpoints.up('md')]: {
            boxShadow: CONTAINER_BOX_SHADOW,
            minHeight: 600,
          },
        },
        container: {
          background: WHITE,
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        },
        eventCarouselContainer: {
          flexGrow: 1,
        },
        sectionHeaderText: {
          fontWeight: 'bold',
          color: PRIMARY_BLUE,
          textAlign: 'center',
        },
        sectionHeader: {
          backgroundColor: WHITE,
        },
        createNewEvent: {
          cursor: 'pointer',
        },
        nextWeekButton: {
          margin: '15px 0',
        },
        loading: {
          display: 'flex',
          placeContent: 'center',
          placeItems: 'center',
          marginTop: 120,
        },
        noEvents: {
          padding: '22px 15px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          textAlign: 'center',
          '& > p > a': {
            color: PRIMARY_BLUE,
          },
          '& svg': {
            width: 100,
            height: 'auto',
          },
        },
        noEventsLabel: {
          marginBottom: 10,
        },
        nextEventButton: {
          minWidth: 124,
          height: 28,
          fontSize: 12,
          marginTop: 15,
        },
        filter: {
          display: 'flex',
          flexFlow: 'row-reverse',
          padding: '15px 15px 0',
        },
      };
    },
    { name: 'EventListWrapper' },
  );
const swipeConfidenceThreshold = 10000;
const swipePower = (offset: number, velocity: number) => Math.abs(offset) * velocity;

const variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0,
  }),
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    zIndex: 0,
    x: direction < 0 ? 1000 : -1000,
    opacity: 0,
  }),
};

const transition = {
  x: { type: 'sprint', stiffness: 300, damping: 30 },
  opacity: { duration: 0.2 },
};

interface IEventListWrapper {
  calendarFormat?: CalendarFormat;
  infoSection?: React.ReactNode;
}

export const EventListWrapper = ({
  infoSection,
  calendarFormat = CalendarFormat.week,
}: IEventListWrapper) => {
  const eventIndex = 0;
  const [[page, direction], setPage] = useState([0, 0]);

  const {
    events,
    calendarController,
    onTodayClick,
    onNextEventClick,
    dailyEventCount,
    noEventsThisWeek,
    eventsLoading,
    goToNextDay,
    goToNextWeek,
  } = useEventListWrapper({ calendarFormat });
  const hasGlobalWarning = useSelector(selectCurrentWarning);
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { classes } = useStyles(hasGlobalWarning)();
  const currentLanguage = useSelector(selectLanguage);

  const handleSwipe = (newDirection: number) => {
    setPage([page + newDirection, newDirection]);
    goToNextDay(newDirection);
  };

  const onDragEnd = (_e: MouseEvent | TouchEvent | PointerEvent, { offset, velocity }: PanInfo) => {
    const swipe = swipePower(offset.x, velocity.x);

    if (swipe < -swipeConfidenceThreshold) {
      handleSwipe(1);
    } else if (swipe > swipeConfidenceThreshold) {
      handleSwipe(-1);
    }
  };

  const showEventPlaceHolder = useMemo(
    () => !eventsLoading && (!events || events[eventIndex]?.length === 0 || events.length === 0),
    [eventsLoading, events, eventIndex],
  );

  return (
    <PrivateLayout backgroundColor={WHITE}>
      {infoSection}
      <div className={classes.dashboardContainer}>
        <motion.div className={classes.root}>
          <CalendarComponent
            calendarController={calendarController}
            dailyEventCount={dailyEventCount}
            onTodayClick={onTodayClick}
            currentLanguage={currentLanguage}
          />
          <div className={classes.container}>
            <AnimatePresence custom={direction} initial={false}>
              <motion.div
                animate="center"
                className={classes.eventCarouselContainer}
                custom={direction}
                drag={isMobile ? 'x' : undefined}
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={1}
                exit="exit"
                initial="enter"
                key={page}
                onDragEnd={onDragEnd}
                transition={transition}
                variants={variants}
              >
                {showEventPlaceHolder ? (
                  <EventEmptyPlaceholder
                    noEventsThisWeek={noEventsThisWeek}
                    onNextEventClick={onNextEventClick}
                    classes={classes}
                  />
                ) : (
                  <>
                    <EventListView events={events} currentDate={calendarController.selectedDay} />
                    {!eventsLoading && (
                      <div className={classes.noEvents}>
                        <Button
                          className={`${classes.nextEventButton} ${classes.nextWeekButton}`}
                          color="primary"
                          onClick={goToNextWeek}
                          variant="contained"
                        >
                          {t('app.eventlist.nextweek')}
                        </Button>
                      </div>
                    )}
                  </>
                )}
                {eventsLoading && (
                  <div className={classes.loading}>
                    <Dots />
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
        <Checklist />
      </div>
    </PrivateLayout>
  );
};
